import {
  OakBox,
  OakFlex,
  OakGrid,
  OakGridArea,
  OakHeading,
  OakMaxWidth,
  OakP,
  OakSecondaryButton,
  OakVideo,
} from "@oaknational/oak-components";

import { aboutUsAccessed } from "@/browser-lib/avo/Avo";
import BlogAndWebinarList from "@/components/GenericPagesComponents/BlogAndWebinarList";
import NewsletterFormWrap from "@/components/GenericPagesComponents/NewsletterFormWrap";
import usePostList from "@/components/SharedComponents/PostList/usePostList";
import { useNewsletterForm } from "@/components/GenericPagesComponents/NewsletterForm";
import { PostListItemProps } from "@/components/SharedComponents/PostListItem";
import { blogToPostListItem } from "@/components/GenericPagesViews/BlogIndex.view";
import { SerializedPost } from "@/pages-helpers/home/getBlogPosts";
import { webinarToPostListItem } from "@/components/GenericPagesViews/WebinarsIndex.view";
import { Testimonials } from "@/components/GenericPagesComponents/Testimonials";
import { HomePage } from "@/common-lib/cms-types";
import { CampaignPromoBanner } from "@/components/GenericPagesComponents/CampaignPromoBanner/CampaignPromoBanner";
import { campaignTextStyles } from "@/pages/campaigns/[campaignSlug]";
import { CampaignPromoBannerType } from "@/common-lib/cms-types/campaignPage";
import { resolveOakHref } from "@/common-lib/urls";
import { buildAboutUsAnalytics } from "@/utils/analytics-builders";
import VideoPlayer from "@/components/SharedComponents/VideoPlayer/VideoPlayer";
import { useTeacherBrowseAnalytics } from "@/context/TeacherBrowseAnalytics/TeacherBrowseAnalyticsProvider";

export const postToPostListItem = (post: SerializedPost): PostListItemProps => {
  return post.type === "blog-post"
    ? blogToPostListItem(post)
    : webinarToPostListItem(post);
};

export type HomePageLowerViewProps = {
  posts: SerializedPost[];
  testimonials: HomePage["testimonials"];
  campaignPromoBanner?: CampaignPromoBannerType | null;
  introVideo?: HomePage["intro"];
};

export const HomePageLowerView = (props: HomePageLowerViewProps) => {
  const { campaignPromoBanner } = props;

  const posts = props.posts.map(postToPostListItem);
  const blogListProps = usePostList({ items: posts, withImage: true });
  const { introVideo } = props;

  const { newsletterSignUpCompleted } = useTeacherBrowseAnalytics(
    (store) => store.track,
  );
  const newsletterFormProps = useNewsletterForm({
    onSubmit: newsletterSignUpCompleted,
  });

  const showCampaignBanner = campaignPromoBanner?.media[0];

  return (
    <>
      {props.testimonials &&
        introVideo?.mediaType === "video" &&
        introVideo.video && (
          <OakMaxWidth>
            <OakGrid
              $pt={["spacing-56", "spacing-64"]}
              $pb={["spacing-56", "spacing-72"]}
              $ph={["spacing-16", "spacing-24"]}
              $rg={["spacing-56", null]}
            >
              <OakGridArea
                $colSpan={[12, 12, 8]}
                $pr={[null, null, "spacing-72"]}
              >
                <OakFlex
                  $flexDirection={"column"}
                  $gap={["spacing-32", "spacing-32", "spacing-48"]}
                >
                  <OakFlex $gap={"spacing-16"} $flexDirection={"column"}>
                    <OakBox>
                      <OakHeading
                        tag="h2"
                        $font={["heading-5", "heading-5", "heading-4"]}
                      >
                        {introVideo?.title}
                      </OakHeading>
                    </OakBox>
                    <OakP $font={["body-2", "body-2", "body-1"]}>
                      {introVideo?.bodyPortableText?.[0]?.children[0]?.text}
                    </OakP>
                    <OakFlex $flexWrap="wrap" $gap="spacing-16">
                      <OakSecondaryButton
                        element="a"
                        href={resolveOakHref({ page: "about-who-we-are" })}
                        iconName="arrow-right"
                        isTrailingIcon={true}
                        onClick={() =>
                          aboutUsAccessed(buildAboutUsAnalytics("about_oak"))
                        }
                      >
                        About Oak
                      </OakSecondaryButton>
                      <OakSecondaryButton
                        element="a"
                        href={resolveOakHref({ page: "about-oaks-impact" })}
                        iconName="arrow-right"
                        isTrailingIcon={true}
                        onClick={() =>
                          aboutUsAccessed(buildAboutUsAnalytics("oaks_impact"))
                        }
                      >
                        Oak's impact
                      </OakSecondaryButton>
                    </OakFlex>
                  </OakFlex>
                  <OakVideo
                    showTranscript={true}
                    transcript={introVideo.video.transcript}
                    videoSlot={
                      <VideoPlayer
                        playbackPolicy="public"
                        playbackId={introVideo.video.video.asset.playbackId}
                        thumbnailTime={introVideo.video.video.asset.thumbTime}
                        title={introVideo.title}
                        location="marketing"
                        omitBorder={true}
                      />
                    }
                  />
                </OakFlex>
              </OakGridArea>
              <OakGridArea
                $colSpan={[12, 12, 4]}
                $alignSelf="start"
                $height={["auto", "auto", "spacing-800"]}
              >
                <Testimonials testimonials={props.testimonials} />
              </OakGridArea>
            </OakGrid>
          </OakMaxWidth>
        )}
      {showCampaignBanner && (
        <OakMaxWidth>
          <OakBox
            $borderRadius={"border-radius-xl"}
            $background={"bg-decorative5-very-subdued"}
          >
            <CampaignPromoBanner
              textStyles={campaignTextStyles}
              heading={campaignPromoBanner?.headingPortableTextWithPromo}
              subheading={campaignPromoBanner?.subheadingPortableTextWithPromo}
              buttonCta={campaignPromoBanner.buttonCta}
              media={campaignPromoBanner.media[0]!}
              buttonUrl={campaignPromoBanner.buttonUrl}
            />
          </OakBox>
        </OakMaxWidth>
      )}
      <OakMaxWidth>
        <BlogAndWebinarList
          blogListPosts={blogListProps}
          showImageOnTablet={true}
          backgroundColor="bg-primary"
          displayOnPhone={true}
          isBackgroundWhite={true}
          title={"Stay up to date"}
        />
      </OakMaxWidth>
      <OakFlex $background={"bg-decorative3-subdued"} $width={"100%"}>
        <OakMaxWidth
          $alignItems={"center"}
          $background={"bg-decorative3-subdued"}
          $mt={"spacing-56"}
          $mb={"spacing-80"}
          $ph={"spacing-16"}
        >
          <OakFlex $maxWidth={["100%", "spacing-960"]}>
            <NewsletterFormWrap desktopColSpan={6} {...newsletterFormProps} />
          </OakFlex>
        </OakMaxWidth>
      </OakFlex>
    </>
  );
};
